import { useMutation, useQueryClient } from 'react-query';
import { invalidatePlannerTasks } from './invalidatePlannerTasks';
import { useTranslation } from 'react-i18next';
import showSnackbar, { showErrorSnackbar } from 'components/shared/Snackbar';
import { submitBatchTaskAction } from 'RestAPIs/planner-messages';
import { MessageActionType } from '../types';
import { usePlannerMessagesStore } from '../store';

const useSubmitBatchAction = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const clearSelection = usePlannerMessagesStore(
    (state) => state.clearSelection
  );

  const { mutateAsync, isLoading } = useMutation(
    (variables: {
      taskIds: string[];
      actionType: MessageActionType;
      note?: string;
      // Bulk-reject summarises both of its calls itself; without this the batch
      // toast would stack on top of that summary.
      silent?: boolean;
    }) => submitBatchTaskAction(variables),
    {
      onSuccess: (response, variables) => {
        const { success, failed } = response.data;

        if (!variables.silent) {
          if (success > 0 && failed === 0) {
            showSnackbar({
              severity: 'success',
              message: t('plannerMessages.bulkActionSuccess')
            });
          } else if (failed > 0) {
            showSnackbar({
              severity: 'warning',
              message: t(
                success > 0
                  ? 'plannerMessages.bulkActionPartlyFailed'
                  : 'plannerMessages.bulkActionAllFailed',
                { failed }
              )
            });
          }
        }

        // Only a fully successful batch may drop the selection: on failure the
        // planner keeps the rows that still need action.
        if (failed === 0) clearSelection();
        invalidatePlannerTasks(queryClient);
      },
      onError: () => showErrorSnackbar()
    }
  );

  return { mutateAsync, isLoading };
};

export default useSubmitBatchAction;
